import { useCallback, useContext, useMemo } from 'react';
import { useService } from '@core/inversify-react';
import { FormikHelpers } from 'formik';
import { message } from 'antd';
import { useBus } from '@core/event-bus';

import { useCashRegisterOperationById } from './';
import { CreateCashRegisterOperationDto } from '../dtos';
import { CreateCashRegisterOperationDtoMapper } from '../mappers';
import { CreateCashRegisterOperationContext } from '../contexts';
import { CreateCashRegisterOperationRepo, CreateCashRegisterOperationRepoType } from '../repos';

export const useCreateCashRegisterOperation = () => {
  const { id, onSucceed, onCancel } = useContext(CreateCashRegisterOperationContext);
  const operation = useCashRegisterOperationById(id, {
    enabled: !!id,
    onError: async (error) => {
      message.error(error.message);
      onCancel();
    },
  });

  const createCourierRepo = useService<CreateCashRegisterOperationRepo>(CreateCashRegisterOperationRepoType);
  const eventBus = useBus();

  const initialValues = useMemo<CreateCashRegisterOperationDto | undefined>(() => {
    if (id) {
      return operation.data ? CreateCashRegisterOperationDtoMapper.toDomain(operation.data) : undefined;
    }

    return { name: '', parentId: '' };
  }, [id, operation.data]);

  const onSubmit = useCallback(
    async (values: CreateCashRegisterOperationDto, helpers: FormikHelpers<CreateCashRegisterOperationDto>) => {
      helpers.setStatus(null);
      const result = await createCourierRepo.execute(values, id || undefined);

      if (result.status === 200) {
        if (!id) {
          eventBus.publish({ type: '@cashRegisters/operations/create/succeed' });
        } else {
          eventBus.publish({ type: '@cashRegisters/operations/update/succeed' });
        }

        onSucceed?.();
      } else if (result.status === 422) {
        helpers.setErrors(result.response);
      } else {
        message.error(result.response);
      }
    },
    [createCourierRepo, eventBus, id, onSucceed],
  );

  return { onSubmit, initialValues };
};
